// Mission Control — Communications Hub.
// Vendor-agnostic (wraps Twilio today, swappable to RingCentral/CloudTalk).
// Sub-views: Inbox · SMS · Calls · Missed · Templates · Auto-Replies
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Inbox, MessageSquare, Phone, PhoneMissed, FileText, Repeat, Plug,
  Send, X, Plus, ToggleLeft, ToggleRight, Search, Clock, ArrowUpRight, ArrowDownLeft, CheckCircle2, Mic,
} from "lucide-react";
import { API, RED, NEON, AMBER, PURPLE, ago, authHeader } from "./shared";

const SUB_TABS = [
  { key: "inbox",       label: "Inbox",        Icon: Inbox },
  { key: "sms",         label: "SMS History",  Icon: MessageSquare },
  { key: "calls",       label: "Call History", Icon: Phone },
  { key: "missed",      label: "Missed Calls", Icon: PhoneMissed },
  { key: "templates",   label: "Templates",    Icon: FileText },
  { key: "rules",       label: "Auto-Replies", Icon: Repeat },
];

export function CommsHub({ token }) {
  const [sub, setSub] = useState("inbox");
  const [overview, setOverview] = useState(null);
  const [provider, setProvider] = useState(null);
  const [showComposer, setShowComposer] = useState(false);

  const refresh = useCallback(() => {
    fetch(`${API}/comms/overview`, { headers: authHeader(token) })
      .then((r) => r.ok ? r.json() : null).then(setOverview).catch(() => {});
    fetch(`${API}/comms/provider`, { headers: authHeader(token) })
      .then((r) => r.ok ? r.json() : null).then(setProvider).catch(() => {});
  }, [token]);

  useEffect(() => { refresh(); const id = setInterval(refresh, 6000); return () => clearInterval(id); }, [refresh]);

  return (
    <div className="space-y-4" data-testid="comms-hub">
      {/* Provider band — proves vendor-replaceability */}
      <div className="rounded-sm border border-white/10 bg-black/50 px-5 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
        data-testid="comms-provider-band">
        <div className="inline-flex items-center gap-2 flex-wrap">
          <Plug size={14} color={NEON} />
          <span className="text-[10px] tracking-[0.18em] font-bold text-white/45">PROVIDER</span>
          <span className="font-heavy text-[14px] tracking-tight text-white uppercase">{provider?.active || "—"}</span>
          <span className="px-1.5 py-0.5 rounded-sm border text-[9.5px] font-extrabold tracking-[0.14em]"
            style={{
              borderColor: provider?.health?.configured ? NEON : AMBER,
              color:       provider?.health?.configured ? NEON : AMBER,
              background:  provider?.health?.configured ? "rgba(66,255,176,0.10)" : "rgba(255,180,0,0.10)",
            }}>
            {provider?.health?.mode === "live" ? "LIVE" : "MOCKED"}
          </span>
          <span className="text-[10.5px] text-white/55 font-mono ml-2 hidden md:inline">
            {(provider?.capabilities || []).join(" · ")}
          </span>
        </div>
        <div className="text-[10px] tracking-[0.14em] font-bold text-white/45">
          SWAPPABLE TO: {(provider?.swappable_to || []).filter((p) => p !== provider?.active).join(" / ").toUpperCase()}
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Mini label="INBOX UNREAD" Icon={Inbox}        val={overview?.inbox_unread ?? "—"}    accent={RED}    testid="comms-kpi-unread" />
        <Mini label="SMS TODAY"    Icon={MessageSquare} val={overview?.sms_today ?? "—"}        accent={NEON}   testid="comms-kpi-today" />
        <Mini label="SMS TOTAL"    Icon={MessageSquare} val={overview?.messages_total ?? "—"}  accent="white"  testid="comms-kpi-total" />
        <Mini label="CALLS"        Icon={Phone}         val={overview?.calls_total ?? "—"}     accent="white"  testid="comms-kpi-calls" />
        <Mini label="MISSED"       Icon={PhoneMissed}   val={overview?.missed_total ?? "—"}    accent={AMBER}  testid="comms-kpi-missed" />
        <Mini label="TEMPLATES"    Icon={FileText}      val={overview?.templates_total ?? "—"} accent="white"  testid="comms-kpi-templates" />
        <Mini label="AUTO-REPLY"   Icon={Repeat}        val={overview?.auto_reply_rules_active ?? "—"} accent={PURPLE} testid="comms-kpi-rules" />
      </div>

      {/* Sub-tab nav */}
      <div className="rounded-sm border border-white/10 bg-black/50">
        <div className="px-3 sm:px-5 py-2.5 border-b border-white/10 flex flex-wrap items-center gap-2 justify-between">
          <div className="flex flex-wrap gap-1.5">
            {SUB_TABS.map(({ key, label, Icon }) => {
              const active = sub === key;
              return (
                <button
                  key={key}
                  onClick={() => setSub(key)}
                  data-testid={`comms-tab-${key}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-extrabold tracking-[0.12em] uppercase rounded-sm border transition-colors"
                  style={{
                    borderColor: active ? RED : "rgba(255,255,255,0.10)",
                    background:  active ? "rgba(255,42,60,0.10)" : "transparent",
                    color:       active ? RED : "rgba(255,255,255,0.75)",
                  }}
                >
                  <Icon size={11} /> {label}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => setShowComposer(true)}
            data-testid="comms-new-sms-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-extrabold tracking-[0.14em] uppercase border"
            style={{ background: RED, borderColor: RED, color: "white", boxShadow: "0 6px 16px rgba(255,42,60,0.30)" }}
          >
            <Send size={11} /> Send SMS
          </button>
        </div>
        {sub === "inbox"     && <InboxView token={token} onComposer={() => setShowComposer(true)} />}
        {sub === "sms"       && <SmsHistoryView token={token} />}
        {sub === "calls"     && <CallHistoryView token={token} missed={false} />}
        {sub === "missed"    && <CallHistoryView token={token} missed={true} />}
        {sub === "templates" && <TemplatesView token={token} onChange={refresh} />}
        {sub === "rules"     && <AutoReplyView token={token} onChange={refresh} />}
      </div>

      {showComposer && (
        <SmsComposer token={token} onClose={() => setShowComposer(false)} onSent={refresh} />
      )}
    </div>
  );
}

function Mini({ label, Icon, val, accent, testid }) {
  return (
    <div className="rounded-sm border border-white/10 bg-black/40 px-3 py-2.5 min-w-0" data-testid={testid}>
      <div className="flex items-center justify-between">
        <Icon size={11} className="text-white/45" />
        <span className="text-[9px] tracking-[0.16em] font-bold text-white/45 truncate">{label}</span>
      </div>
      <p className="font-heavy mt-1 leading-none truncate" style={{ color: accent, fontSize: "22px" }}>{val}</p>
    </div>
  );
}

/* ──────────  INBOX  ────────── */
function InboxView({ token, onComposer }) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    let live = true;
    const load = () =>
      fetch(`${API}/comms/inbox?limit=80`, { headers: authHeader(token) })
        .then((r) => r.ok ? r.json() : []).then((d) => { if (live) setThreads(d || []); });
    load();
    // Pause polling while a conversation is open so the composer state never gets
    // clobbered by an inbound re-render. ConversationPane has its own poll.
    if (active) return () => { live = false; };
    const id = setInterval(load, 8000);
    return () => { live = false; clearInterval(id); };
  }, [token, active]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-0 max-h-[460px]">
      {/* Threads list */}
      <div className="md:col-span-1 border-r border-white/5 overflow-y-auto max-h-[460px]" data-testid="inbox-threads">
        {threads.length === 0 && <p className="text-center text-white/45 text-[13px] py-12">No conversations yet.</p>}
        {threads.map((t) => {
          const isActive = active?.phone === t.phone;
          return (
            <button
              key={t.phone}
              onClick={() => setActive(t)}
              data-testid={`inbox-thread-${t.phone}`}
              className="w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/[0.04]"
              style={{ background: isActive ? "rgba(255,42,60,0.06)" : "transparent" }}
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[12px] text-white truncate">{t.phone}</span>
                {t.unread > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 rounded-sm text-[9px] font-extrabold tracking-[0.10em]"
                    style={{ background: RED, color: "white" }} data-testid={`thread-unread-${t.phone}`}>
                    {t.unread}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-1">
                {t.last_direction === "inbound"
                  ? <ArrowDownLeft size={10} className="text-white/55 shrink-0" />
                  : <ArrowUpRight  size={10} className="text-white/55 shrink-0" />}
                <p className="text-[11px] text-white/65 truncate">{t.last_message}</p>
              </div>
              <p className="text-[9.5px] tracking-[0.12em] font-bold text-white/35 mt-1 font-mono">{ago(t.last_at)}</p>
            </button>
          );
        })}
      </div>
      {/* Active conversation */}
      <div className="md:col-span-2 max-h-[460px] overflow-y-auto">
        {!active && (
          <div className="h-full flex flex-col items-center justify-center text-center px-6 py-16 gap-3">
            <Inbox size={32} color={NEON} />
            <p className="font-heavy text-white text-[14px]">Select a conversation</p>
            <p className="text-[12px] text-white/55 max-w-sm">All inbound SMS and replies your reps send live here. Click a thread on the left to view the full message history.</p>
            <button onClick={onComposer} className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-extrabold tracking-[0.14em] uppercase border" style={{ borderColor: RED, color: RED }}>
              <Send size={11} /> Start a new SMS
            </button>
          </div>
        )}
        {active && <ConversationPane token={token} thread={active} />}
      </div>
    </div>
  );
}

function ConversationPane({ token, thread }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [templates, setTemplates] = useState([]);
  const inputRef = useRef(null);

  const load = useCallback(() => {
    fetch(`${API}/comms/messages?phone=${encodeURIComponent(thread.phone)}&limit=100`, { headers: authHeader(token) })
      .then((r) => r.ok ? r.json() : []).then((d) => setMsgs(d || []));
  }, [token, thread.phone]);

  useEffect(() => { load(); const id = setInterval(load, 6000); return () => clearInterval(id); }, [load]);
  useEffect(() => {
    fetch(`${API}/comms/templates`, { headers: authHeader(token) })
      .then((r) => r.ok ? r.json() : []).then((d) => setTemplates(d || []));
  }, [token]);

  const send = useCallback(async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    // Read live value from the DOM as a guard against any stale state.
    const body = (inputRef.current?.value || text).trim();
    if (!body || sending) return;
    setErr(""); setSending(true);
    try {
      const res = await fetch(`${API}/comms/sms`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader(token) },
        body: JSON.stringify({ to: thread.phone, body, lead_id: thread.lead_id }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setText("");
      if (inputRef.current) inputRef.current.value = "";
      load();
    } catch (e2) {
      setErr(String(e2));
    } finally {
      setSending(false);
    }
  }, [text, sending, token, thread.phone, thread.lead_id, load]);

  return (
    <div className="flex flex-col h-[460px]" data-testid="conversation-pane">
      {/* Pane header */}
      <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
        <div>
          <p className="font-mono text-[13px] text-white">{thread.phone}</p>
          {thread.lead_email && <p className="text-[10.5px] tracking-[0.12em] font-bold text-white/45 mt-0.5">{thread.lead_email}</p>}
        </div>
        <span className="text-[10px] tracking-[0.14em] font-bold text-white/45">{msgs.length} MESSAGES</span>
      </div>
      {/* Messages (newest at bottom) */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2 bg-black/30">
        {msgs.slice().reverse().map((m) => (
          <Bubble key={m.id} msg={m} />
        ))}
      </div>
      {/* Composer */}
      <form onSubmit={send} className="px-3 sm:px-4 py-3 border-t border-white/10 bg-black/40">
        {templates.length > 0 && (
          <div className="flex gap-1.5 mb-2 overflow-x-auto pb-1">
            {templates.map((t) => (
              <button type="button" key={t.id} onClick={() => { setText(t.body); if (inputRef.current) inputRef.current.value = t.body; }}
                data-testid={`composer-template-${t.id.slice(0,8)}`}
                className="px-2 py-1 text-[9.5px] tracking-[0.12em] font-extrabold uppercase rounded-sm border whitespace-nowrap"
                style={{ borderColor: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.75)" }}>
                {t.name}
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input ref={inputRef} value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Type a reply…"
            data-testid="conversation-input"
            className="flex-1 bg-black border border-white/15 px-3 py-2 text-white font-mono text-[12px] focus:border-[#FF2A3C] focus:outline-none" />
          <button type="submit" disabled={sending}
            data-testid="conversation-send"
            className="px-3 py-2 text-[11px] font-extrabold tracking-[0.12em] uppercase border"
            style={{ background: RED, borderColor: RED, color: "white" }}>
            <Send size={11} />
          </button>
        </div>
        {err && <p className="text-[10.5px] mt-1.5 text-[#FF2A3C] font-mono" data-testid="conversation-error">Send failed: {err}</p>}
      </form>
    </div>
  );
}

function Bubble({ msg }) {
  const out = msg.direction === "outbound";
  return (
    <div className={`flex ${out ? "justify-end" : "justify-start"}`}>
      <div className="max-w-[78%]">
        <div
          className="px-3 py-2 rounded-sm text-[12.5px] leading-relaxed"
          style={{
            background: out ? "rgba(255,42,60,0.18)" : "rgba(255,255,255,0.06)",
            border: `1px solid ${out ? "rgba(255,42,60,0.40)" : "rgba(255,255,255,0.10)"}`,
            color: "white",
          }}
        >
          {msg.body}
        </div>
        <div className={`flex items-center gap-1.5 mt-1 ${out ? "justify-end" : "justify-start"}`}>
          {msg.user_email === "auto-reply" && (
            <span className="text-[9px] tracking-[0.12em] font-extrabold uppercase" style={{ color: PURPLE }}>auto</span>
          )}
          <span className="text-[9.5px] text-white/40 font-mono">{ago(msg.created_at)}</span>
          {out && msg.status === "sent" && <CheckCircle2 size={9} className="text-white/40" />}
        </div>
      </div>
    </div>
  );
}

/* ──────────  SMS HISTORY  ────────── */
function SmsHistoryView({ token }) {
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    fetch(`${API}/comms/messages?limit=200`, { headers: authHeader(token) })
      .then((r) => r.ok ? r.json() : []).then(setMsgs).catch(() => {});
  }, [token]);

  const filtered = useMemo(() => {
    if (!q.trim()) return msgs;
    const ql = q.toLowerCase();
    return msgs.filter((m) =>
      (m.body || "").toLowerCase().includes(ql) ||
      (m.from || "").includes(ql) ||
      (m.to || "").includes(ql) ||
      (m.lead_email || "").toLowerCase().includes(ql),
    );
  }, [msgs, q]);

  return (
    <div data-testid="sms-history">
      <div className="px-3 sm:px-5 py-2.5 border-b border-white/5 flex items-center gap-2">
        <Search size={12} className="text-white/45" />
        <input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by body / phone / email…"
          data-testid="sms-history-search"
          className="flex-1 bg-transparent text-[12px] text-white placeholder-white/35 focus:outline-none font-mono" />
        <span className="text-[10px] tracking-[0.14em] font-bold text-white/45">{filtered.length}</span>
      </div>
      <div className="max-h-[480px] overflow-y-auto divide-y divide-white/5">
        {filtered.length === 0 && <p className="text-center text-white/45 text-[13px] py-12">No messages.</p>}
        {filtered.map((m) => (
          <div key={m.id} className="grid grid-cols-12 gap-3 px-3 sm:px-5 py-2.5" data-testid={`sms-row-${m.id.slice(0,8)}`}>
            <div className="col-span-2 sm:col-span-1 flex items-center">
              {m.direction === "outbound"
                ? <ArrowUpRight size={11} color={RED} />
                : <ArrowDownLeft size={11} color={NEON} />}
            </div>
            <div className="col-span-10 sm:col-span-3 font-mono text-[11px] text-white truncate">
              {m.direction === "outbound" ? `→ ${m.to}` : `← ${m.from}`}
            </div>
            <div className="col-span-12 sm:col-span-6 text-[12px] text-white/80 truncate">{m.body}</div>
            <div className="col-span-12 sm:col-span-2 text-right text-[10.5px] font-mono text-white/45">{ago(m.created_at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ──────────  CALL HISTORY  ────────── */
function CallHistoryView({ token, missed }) {
  const [calls, setCalls] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    const url = `${API}/comms/calls?limit=200${missed ? "&missed=true" : ""}`;
    fetch(url, { headers: authHeader(token) })
      .then((r) => r.ok ? r.json() : []).then(setCalls).catch(() => {});
  }, [token, missed]);

  useEffect(() => { load(); }, [load]);

  const statusColor = (s) => ({
    completed:   NEON,
    in_progress: AMBER,
    queued:      AMBER,
    ringing:     AMBER,
    missed:      RED,
    failed:      RED,
  }[s] || "white");

  return (
    <div data-testid={missed ? "missed-calls" : "call-history"}>
      <div className="px-3 sm:px-5 py-2.5 border-b border-white/5 flex items-center justify-between">
        <p className="text-[11px] tracking-[0.14em] font-bold text-white/55">
          {missed ? "MISSED CALLS — FOLLOW UP REQUIRED" : "ALL CALLS"}
        </p>
        <span className="text-[10px] tracking-[0.14em] font-bold text-white/45">{calls.length}</span>
      </div>
      <div className="max-h-[480px] overflow-y-auto divide-y divide-white/5">
        {calls.length === 0 && <p className="text-center text-white/45 text-[13px] py-12">{missed ? "No missed calls. Great work." : "No calls yet."}</p>}
        {calls.map((c) => (
          <button key={c.id} onClick={() => setSelected(c)}
            data-testid={`call-row-${c.id.slice(0,8)}`}
            className="w-full grid grid-cols-12 gap-3 px-3 sm:px-5 py-3 hover:bg-white/[0.04] text-left">
            <div className="col-span-2 sm:col-span-1 flex items-center">
              {c.direction === "outbound"
                ? <ArrowUpRight size={12} color={RED} />
                : <ArrowDownLeft size={12} color={NEON} />}
            </div>
            <div className="col-span-10 sm:col-span-3 font-mono text-[11.5px] text-white truncate">
              {c.direction === "outbound" ? `→ ${c.to}` : `← ${c.from}`}
            </div>
            <div className="col-span-6 sm:col-span-2">
              <span className="px-1.5 py-0.5 rounded-sm text-[9.5px] font-extrabold tracking-[0.10em] uppercase"
                style={{ borderColor: statusColor(c.status), color: statusColor(c.status), background: `${statusColor(c.status)}10`, border: `1px solid ${statusColor(c.status)}` }}>
                {c.status}
              </span>
            </div>
            <div className="col-span-3 sm:col-span-2 text-[11px] text-white/65 font-mono">
              {c.duration_sec ? `${Math.floor(c.duration_sec/60)}:${String(c.duration_sec%60).padStart(2,"0")}` : "—"}
            </div>
            <div className="hidden sm:block col-span-2 text-[10.5px] text-white/55 font-mono truncate">{c.notes?.length || 0} note{(c.notes?.length || 0) === 1 ? "" : "s"}</div>
            <div className="col-span-3 sm:col-span-2 text-right text-[10.5px] font-mono text-white/45">{ago(c.created_at)}</div>
          </button>
        ))}
      </div>
      {selected && <CallDrawer token={token} call={selected} onClose={() => setSelected(null)} onChanged={load} />}
    </div>
  );
}

function CallDrawer({ token, call, onClose, onChanged }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [latest, setLatest] = useState(call);

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    const res = await fetch(`${API}/comms/calls/${latest.id}/note`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader(token) },
      body: JSON.stringify({ note }),
    });
    if (res.ok) setLatest(await res.json());
    setBusy(false); setNote(""); onChanged && onChanged();
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="call-drawer">
      <button onClick={onClose} className="flex-1 bg-black/60 backdrop-blur-sm" aria-label="close" />
      <aside className="w-full sm:w-[460px] bg-[#0A0B10] border-l border-white/10 h-full overflow-y-auto">
        <header className="sticky top-0 bg-[#0A0B10]/95 backdrop-blur border-b border-white/10 px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-[0.18em] font-bold text-white/45">CALL DETAIL</p>
            <p className="font-heavy text-white text-[16px] mt-0.5 font-mono">
              {call.direction === "outbound" ? "→" : "←"} {call.direction === "outbound" ? call.to : call.from}
            </p>
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-sm border border-white/15 text-white/70 hover:text-white hover:border-[#FF2A3C]" data-testid="call-drawer-close">
            <X size={16} />
          </button>
        </header>
        <div className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-2.5">
            <Cell label="STATUS"    value={call.status} />
            <Cell label="DURATION"  value={call.duration_sec ? `${Math.floor(call.duration_sec/60)}m ${call.duration_sec%60}s` : "—"} />
            <Cell label="PROVIDER"  value={call.provider} />
            <Cell label="SID"       value={call.provider_sid} mono />
          </div>
          {call.recording_url && (
            <a href={call.recording_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-2 text-[11px] font-extrabold tracking-[0.14em] uppercase border" style={{ borderColor: NEON, color: NEON }}>
              <Mic size={11} /> Listen to recording
            </a>
          )}
          {/* Notes log */}
          <section className="rounded-sm border border-white/10 bg-black/40">
            <header className="px-4 py-2.5 border-b border-white/10 flex items-center justify-between">
              <p className="font-heavy text-[12px] inline-flex items-center gap-2"><FileText size={12} color={AMBER} /> CALL NOTES</p>
              <span className="text-[10px] tracking-[0.16em] font-bold text-white/45">{latest.notes?.length || 0}</span>
            </header>
            <div className="divide-y divide-white/5 max-h-[200px] overflow-y-auto">
              {(latest.notes || []).length === 0 && <p className="px-4 py-5 text-center text-white/45 text-[11.5px]">No notes logged yet.</p>}
              {(latest.notes || []).slice().reverse().map((n) => (
                <div key={n.id} className="px-4 py-2">
                  <p className="text-[12px] text-white">{n.note}</p>
                  <p className="text-[9.5px] text-white/45 font-mono mt-0.5">{n.author} · {ago(n.created_at)}</p>
                </div>
              ))}
            </div>
            <div className="p-3 flex gap-2 border-t border-white/5">
              <input value={note} onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addNote()}
                placeholder="Add a note (outcome, next step…)"
                data-testid="call-note-input"
                className="flex-1 bg-black border border-white/15 px-3 py-2 text-white font-mono text-[12px] focus:border-[#FFB400] focus:outline-none" />
              <button onClick={addNote} disabled={busy || !note.trim()}
                data-testid="call-note-add"
                className="px-3 py-2 text-[11px] font-extrabold tracking-[0.12em] uppercase border"
                style={{ background: AMBER, borderColor: AMBER, color: "black" }}>
                Add
              </button>
            </div>
          </section>
        </div>
      </aside>
    </div>
  );
}

function Cell({ label, value, mono }) {
  return (
    <div className="border border-white/10 bg-black/40 px-3 py-2 rounded-sm">
      <p className="text-[9px] tracking-[0.16em] font-bold text-white/45">{label}</p>
      <p className={`text-white mt-1 ${mono ? "font-mono text-[10.5px]" : "font-extrabold text-[12.5px]"} truncate`}>{value || "—"}</p>
    </div>
  );
}

/* ──────────  TEMPLATES  ────────── */
function TemplatesView({ token, onChange }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [body, setBody] = useState("");

  const load = useCallback(() => {
    fetch(`${API}/comms/templates`, { headers: authHeader(token) })
      .then((r) => r.ok ? r.json() : []).then(setItems).catch(() => {});
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim() || !body.trim()) return;
    await fetch(`${API}/comms/templates`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader(token) },
      body: JSON.stringify({ name, body }),
    });
    setName(""); setBody(""); load(); onChange && onChange();
  };

  const del = async (id) => {
    await fetch(`${API}/comms/templates/${id}`, { method: "DELETE", headers: authHeader(token) });
    load(); onChange && onChange();
  };

  return (
    <div data-testid="templates-view" className="grid grid-cols-1 md:grid-cols-2 gap-0">
      {/* Create */}
      <div className="md:border-r border-white/5 p-4 space-y-3">
        <p className="font-heavy text-[12px] tracking-[0.14em] text-white/65 uppercase inline-flex items-center gap-2"><Plus size={11} color={NEON} /> CREATE TEMPLATE</p>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Template name (e.g. Voicemail)"
          data-testid="tpl-name"
          className="w-full bg-black border border-white/15 px-3 py-2 text-white text-[12px] font-mono focus:border-[#42FFB0] focus:outline-none" />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Message body — supports {first_name} substitution"
          data-testid="tpl-body" rows={4}
          className="w-full bg-black border border-white/15 px-3 py-2 text-white text-[12px] focus:border-[#42FFB0] focus:outline-none resize-none" />
        <button onClick={create} disabled={!name.trim() || !body.trim()}
          data-testid="tpl-create"
          className="w-full px-3 py-2 text-[11px] font-extrabold tracking-[0.14em] uppercase border"
          style={{ background: NEON, borderColor: NEON, color: "black" }}>
          Save Template
        </button>
      </div>
      {/* List */}
      <div className="max-h-[420px] overflow-y-auto">
        {items.length === 0 && <p className="text-center text-white/45 text-[12px] py-10">No templates yet.</p>}
        {items.map((t) => (
          <div key={t.id} className="px-4 py-3 border-b border-white/5" data-testid={`tpl-row-${t.id.slice(0,8)}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-heavy text-[12.5px] text-white truncate">{t.name}</p>
                <p className="text-[11.5px] text-white/60 mt-1 leading-relaxed">{t.body}</p>
              </div>
              <button onClick={() => del(t.id)}
                data-testid={`tpl-delete-${t.id.slice(0,8)}`}
                className="text-white/45 hover:text-[#FF2A3C] shrink-0">
                <X size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ──────────  AUTO-REPLIES  ────────── */
function AutoReplyView({ token, onChange }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [trigger, setTrigger] = useState("keyword");
  const [match, setMatch] = useState("");
  const [body, setBody] = useState("");

  const load = useCallback(() => {
    fetch(`${API}/comms/auto-replies`, { headers: authHeader(token) })
      .then((r) => r.ok ? r.json() : []).then(setItems).catch(() => {});
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim() || !body.trim()) return;
    await fetch(`${API}/comms/auto-replies`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader(token) },
      body: JSON.stringify({ name, trigger, match, body, enabled: true }),
    });
    setName(""); setMatch(""); setBody(""); load(); onChange && onChange();
  };

  const toggle = async (r) => {
    await fetch(`${API}/comms/auto-replies/${r.id}?enabled=${!r.enabled}`, {
      method: "PATCH", headers: authHeader(token),
    });
    load(); onChange && onChange();
  };

  const del = async (id) => {
    await fetch(`${API}/comms/auto-replies/${id}`, { method: "DELETE", headers: authHeader(token) });
    load(); onChange && onChange();
  };

  return (
    <div data-testid="auto-reply-view" className="grid grid-cols-1 md:grid-cols-2 gap-0">
      {/* Create */}
      <div className="md:border-r border-white/5 p-4 space-y-3">
        <p className="font-heavy text-[12px] tracking-[0.14em] text-white/65 uppercase inline-flex items-center gap-2"><Plus size={11} color={PURPLE} /> NEW AUTO-REPLY</p>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Rule name"
          data-testid="rule-name"
          className="w-full bg-black border border-white/15 px-3 py-2 text-white text-[12px] focus:border-[#7C5CFF] focus:outline-none" />
        <select value={trigger} onChange={(e) => setTrigger(e.target.value)}
          data-testid="rule-trigger"
          className="w-full bg-black border border-white/15 px-3 py-2 text-white text-[12px]">
          <option value="keyword">Keyword match</option>
          <option value="business_hours">Business-hours window</option>
          <option value="always">Always (every inbound)</option>
        </select>
        {trigger === "keyword" && (
          <input value={match} onChange={(e) => setMatch(e.target.value)} placeholder="Match string (case-insensitive)"
            data-testid="rule-match"
            className="w-full bg-black border border-white/15 px-3 py-2 text-white text-[12px] font-mono" />
        )}
        {trigger === "business_hours" && (
          <select value={match} onChange={(e) => setMatch(e.target.value)}
            data-testid="rule-hours"
            className="w-full bg-black border border-white/15 px-3 py-2 text-white text-[12px]">
            <option value="off">Send only OUTSIDE business hours</option>
            <option value="on">Send only DURING business hours</option>
          </select>
        )}
        <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Reply body"
          data-testid="rule-body" rows={3}
          className="w-full bg-black border border-white/15 px-3 py-2 text-white text-[12px] resize-none" />
        <button onClick={create} disabled={!name.trim() || !body.trim()}
          data-testid="rule-create"
          className="w-full px-3 py-2 text-[11px] font-extrabold tracking-[0.14em] uppercase border"
          style={{ background: PURPLE, borderColor: PURPLE, color: "white" }}>
          Save Rule
        </button>
      </div>
      {/* List */}
      <div className="max-h-[460px] overflow-y-auto">
        {items.length === 0 && <p className="text-center text-white/45 text-[12px] py-10">No auto-replies yet.</p>}
        {items.map((r) => (
          <div key={r.id} className="px-4 py-3 border-b border-white/5" data-testid={`rule-row-${r.id.slice(0,8)}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-heavy text-[12.5px] text-white truncate">{r.name}</p>
                  <span className="text-[9.5px] tracking-[0.14em] font-extrabold uppercase px-1.5 py-0.5 rounded-sm border"
                    style={{ borderColor: PURPLE, color: PURPLE }}>
                    {r.trigger}{r.match ? ` · ${r.match}` : ""}
                  </span>
                </div>
                <p className="text-[11.5px] text-white/60 mt-1.5 leading-relaxed">{r.body || "(uses linked template)"}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => toggle(r)} data-testid={`rule-toggle-${r.id.slice(0,8)}`} title={r.enabled ? "Disable" : "Enable"}>
                  {r.enabled
                    ? <ToggleRight size={20} color={NEON} />
                    : <ToggleLeft  size={20} color="rgba(255,255,255,0.35)" />}
                </button>
                <button onClick={() => del(r.id)} data-testid={`rule-delete-${r.id.slice(0,8)}`} className="text-white/45 hover:text-[#FF2A3C]">
                  <X size={13} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ──────────  SMS COMPOSER (modal)  ────────── */
function SmsComposer({ token, onClose, onSent }) {
  const [to, setTo] = useState("");
  const [body, setBody] = useState("");
  const [templates, setTemplates] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    fetch(`${API}/comms/templates`, { headers: authHeader(token) })
      .then((r) => r.ok ? r.json() : []).then(setTemplates);
  }, [token]);

  const send = async () => {
    if (!to.trim() || !body.trim()) return;
    setSending(true);
    const res = await fetch(`${API}/comms/sms`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader(token) },
      body: JSON.stringify({ to, body }),
    });
    setSending(false);
    if (res.ok) {
      setSent(true);
      onSent && onSent();
      setTimeout(onClose, 800);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-3" data-testid="sms-composer">
      <div className="w-full max-w-md bg-[#0A0B10] border border-white/10 rounded-sm">
        <header className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <p className="font-heavy text-[14px] inline-flex items-center gap-2"><Send size={13} color={RED} /> SEND SMS</p>
          <button onClick={onClose} data-testid="composer-close" className="w-8 h-8 flex items-center justify-center text-white/65 hover:text-white">
            <X size={14} />
          </button>
        </header>
        <div className="px-5 py-4 space-y-3">
          <div>
            <p className="text-[9.5px] tracking-[0.16em] font-bold text-white/45 mb-1.5">TO (E.164)</p>
            <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="+12148155966"
              data-testid="composer-to"
              className="w-full bg-black border border-white/15 px-3 py-2 text-white font-mono text-[12px] focus:border-[#FF2A3C] focus:outline-none" />
          </div>
          <div>
            <p className="text-[9.5px] tracking-[0.16em] font-bold text-white/45 mb-1.5">MESSAGE</p>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Your message…"
              data-testid="composer-body" rows={4}
              className="w-full bg-black border border-white/15 px-3 py-2 text-white text-[12px] focus:border-[#FF2A3C] focus:outline-none resize-none" />
          </div>
          {templates.length > 0 && (
            <div>
              <p className="text-[9.5px] tracking-[0.16em] font-bold text-white/45 mb-1.5">QUICK TEMPLATES</p>
              <div className="flex flex-wrap gap-1.5">
                {templates.map((t) => (
                  <button key={t.id} onClick={() => setBody(t.body)}
                    data-testid={`composer-template-${t.id.slice(0,8)}`}
                    className="px-2 py-1 text-[9.5px] tracking-[0.12em] font-extrabold uppercase rounded-sm border"
                    style={{ borderColor: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.75)" }}>
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <button onClick={send} disabled={sending || !to.trim() || !body.trim()}
            data-testid="composer-send"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-[12px] font-extrabold tracking-[0.14em] uppercase text-white"
            style={{ background: sent ? NEON : RED, color: sent ? "black" : "white" }}>
            {sent ? <><CheckCircle2 size={13} /> Sent</> : <><Send size={13} /> {sending ? "Sending…" : "Send"}</>}
          </button>
          <p className="text-[9.5px] tracking-[0.12em] font-bold text-white/35 text-center">
            <Clock size={9} className="inline -mt-0.5 mr-1" />
            Routes through the active provider · auto-logs to lead timeline
          </p>
        </div>
      </div>
    </div>
  );
}
